<template>
    <!-- Categories Breakdown List-->
    <v-row class="mt-2">
      <v-col cols="12">
        <v-skeleton-loader
          v-if="!dataLoaded"
          type="card"
          height="400"
        ></v-skeleton-loader>
        <v-card v-else-if="Object.keys(categoriesData).length === 0" class="mb-4">
          <v-card-title class="text-subtitle-1 text-sm-h6">{{ $t('projectSummary') }}</v-card-title>
          <v-card-text class="text-center py-8">
            <v-icon size="large" color="grey">mdi-information-outline</v-icon>
            <p class="mt-2">{{ $t('noDataAvailable') }}</p>
          </v-card-text>
        </v-card>
        <v-card v-else class="mb-4">
          <v-card-title class="text-subtitle-1 text-sm-h6">{{ $t('projectSummary') }}</v-card-title>
          <v-card-text >
            <v-expansion-panels >
                <v-expansion-panel
                  v-for="category in categoriesData"
                  :key="category.id"
                >
                  <v-expansion-panel-title>
                    <div class="d-flex flex-column flex-sm-row align-start align-sm-center justify-space-between gap-2 w-100">
                      <div class="d-flex align-center">
                        <v-icon :icon="getIconClass(category.icon)" class="mr-2"/>
                        <span class="text-caption text-sm-body-1">{{ category.title }}</span>
                      </div>
                      <div class="d-flex flex-wrap align-center gap-2">
                        <v-chip
                          v-for="status in getSortedStatuses(statusCounts[category.id]?.statuses || {})"
                          :key="status.statusColor"
                          :color="status.statusColor"
                          label
                          class="px-2 ma-1"
                        >
                          <template v-slot:prepend>
                            <v-icon :color="status.statusColor">{{ status.icon }}</v-icon>
                          </template>
                          <v-tooltip activator="parent" location="top">
                            <span style="white-space: pre-line">{{ status.toolTip }}</span>
                          </v-tooltip>
                          {{ status.count }}
                        </v-chip>
                      </div>
                    </div>
                  </v-expansion-panel-title>
                  <v-expansion-panel-text>
                    <v-list density="compact">
                      <v-list-item
                        v-for="subcategory in getFilteredSubcategories(category.id)"
                        :key="subcategory.id"
                      >
                        <v-list-item-title>
                          <div class="d-flex flex-column flex-sm-row align-start align-sm-center justify-space-between gap-2">
                            <span class="text-caption text-sm-body-2">{{ subcategory.title }}</span>
                            <div class="d-flex flex-wrap align-center gap-2">
                              <v-chip
                                v-for="status in getSortedStatuses(statusCounts[category.id]?.subcategories[subcategory.id]?.statuses || {})"
                                :key="status.statusColor"
                                :color="status.statusColor"
                                label
                                class="px-2 ma-1"
                                @click="openUrl(status.link)"
                                clickable 
                              >
                                <template v-slot:prepend>
                                  <v-icon :color="status.statusColor">{{ status.icon }}</v-icon>
                                </template>
                                <v-tooltip activator="parent" location="top">
                                  <span style="white-space: pre-line">{{ status.toolTip }}</span>
                                </v-tooltip>
                                {{ status.count }}
                              </v-chip>
                            </div>
                          </div>
                        </v-list-item-title>
                      </v-list-item>
                    </v-list>
                  </v-expansion-panel-text>
                </v-expansion-panel>
              </v-expansion-panels>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>
    </template>
    
    <script setup>
    import { computed, ref, onMounted, watch } from 'vue';
    import { useStore } from 'vuex';
    
    const store = useStore();
    const conn = computed(() => store.getters.getDBConnection);
    const settings = computed(() => store.state.settings);
    const filterContext = computed(() => store.state.filterItemsContext);
    const dataLoaded = ref(false);
    
    const statusCounts = ref({});
    const categoriesData = ref({});
    const subcategoriesData = ref({});
    const isMounted = ref(false);
    
    // Get subcategories for a specific category
    const getFilteredSubcategories = (categoryId) => {
      return Object.values(subcategoriesData.value)
        .filter(subcategory => 
          subcategory.categoryId === categoryId && 
          statusCounts.value[categoryId]?.subcategories?.[subcategory.id]
        )
        .sort((a, b) => a.title.localeCompare(b.title));
    };
    
    // Sort statuses by their sort property
    const getSortedStatuses = (statuses) => {
      return Object.values(statuses).sort((a, b) => {
        // Convert to numbers for comparison in case they're strings
        const sortA = Number(a.sort);
        const sortB = Number(b.sort);
        return sortA - sortB;
      });
    };
    
    // Function to open URL in new tab
    const openUrl = (url) => {
      window.open(url, '_blank');
    };
    
    // Get all categories and subcategories from DuckDB
    const getCategoriesAndSubcategories = async () => {
      const categoriesQuery = `
        SELECT DISTINCT
          categoryId AS id,
          categoryTitle AS title,
          c.icon as icon,
          c.sort as sort
        FROM item_templates it
        JOIN categories c ON c.id = it.categoryId
        ORDER By sort
      `;
      
      const subcategoriesQuery = `
        SELECT DISTINCT
          subcategoryId AS id,
          subcategoryTitle AS title,
          categoryId as categoryId,
          type as type
        FROM item_templates
      `;
      
      const [categoriesResult, subcategoriesResult] = await Promise.all([
        executeAndProcessQuery(categoriesQuery),
        executeAndProcessQuery(subcategoriesQuery)
      ]);
      
      // Convert categories array to object with ID as key
      const categoriesMap = {};
      categoriesResult.forEach(category => {
        categoriesMap[category.id] = category;
      });
      
      // Convert subcategories array to object with ID as key
      const subcategoriesMap = {};
      subcategoriesResult.forEach(subcategory => {
        subcategoriesMap[subcategory.id] = subcategory;
      });
      
      return { categories: categoriesMap, subcategories: subcategoriesMap };
    };
    
    // Get category status counts with tooltips and icons
    const getCategoryStatusCounts = async () => {
      const query = `
        WITH status_counts AS (
          SELECT 
            it.categoryId as categoryId,
            s.color AS statusColor,
            s.title AS statusTitle,
            s.sort AS sort,
            COUNT(*) AS value
          FROM items i
          JOIN item_templates it ON i.profileId = it.id
          JOIN status_templates s ON i.StatusId = s.id
          WHERE it.categoryId IS NOT NULL
          GROUP BY it.categoryId, s.color, s.title, s.sort
        )
        SELECT 
          categoryId,
          statusColor,
          statusTitle,
          sort,
          value
        FROM status_counts
        ORDER BY categoryId, sort, statusTitle
      `;
        
      // Get raw data from DuckDB
      const rawData = await executeAndProcessQuery(query);
        
      // Process the data to format it as needed by the component
      const result = {};
        
      rawData.forEach(row => {
        if (!result[row.categoryId]) {
          result[row.categoryId] = { statuses: {}, subcategories: {} };
        }
            
        if (!result[row.categoryId].statuses[row.statusColor]) {
          result[row.categoryId].statuses[row.statusColor] = {
            count: 0,
            titles: {},
            icon: 'mdi-circle',
            toolTip: '',
            statusColor: row.statusColor,
            sort: row.sort
          };
        }
            
        // Add status title and count
        const value = Number(String(row.value));
        result[row.categoryId].statuses[row.statusColor].titles[row.statusTitle] = value;
        result[row.categoryId].statuses[row.statusColor].count += value;
      });
        
      // Format tooltips and icons
      for (const categoryId in result) {
        for (const color in result[categoryId].statuses) {
          const status = result[categoryId].statuses[color];
          status.toolTip = Object.entries(status.titles)
            .map(([title, count]) => `${title}: ${count}`)
            .join('\n');
          status.icon = Object.keys(status.titles).length > 1 ? 'mdi-circle-multiple' : 'mdi-circle';
        }
      }
        
      return result;
    };
    
    // Get subcategory status counts with tooltips, icons, and links
    const getSubcategoryStatusCounts = async () => {
      const query = `
        WITH status_counts AS (
          SELECT 
            it.categoryId as categoryId,
            it.subcategoryId as subcategoryId,
            it.type as type,
            s.color AS statusColor,
            s.title AS statusTitle,
            s.sort AS sort,
            COUNT(*) AS value
          FROM items i
          JOIN item_templates it ON i.profileId = it.id
          JOIN status_templates s ON i.StatusId = s.id
          WHERE it.subcategoryId IS NOT NULL
          GROUP BY it.categoryId, it.subcategoryId, it.type, s.color, s.title, s.sort
        )
        SELECT 
          categoryId,
          subcategoryId,
          type,
          statusColor,
          statusTitle,
          sort,
          value
        FROM status_counts
        ORDER BY subcategoryId, sort, statusTitle
      `;
      
      // Get raw data from DuckDB
      const rawData = await executeAndProcessQuery(query);
      
      // Process the data to format it as needed by the component
      const result = {};
      
      rawData.forEach(row => {
        if (!result[row.categoryId]) {
          result[row.categoryId] = { subcategories: {} };
        }
          
        if (!result[row.categoryId].subcategories[row.subcategoryId]) {
          result[row.categoryId].subcategories[row.subcategoryId] = {
            statuses: {},
            type: row.type
          };
        }
          
        if (!result[row.categoryId].subcategories[row.subcategoryId].statuses[row.statusColor]) {
          result[row.categoryId].subcategories[row.subcategoryId].statuses[row.statusColor] = {
            count: 0,
            titles: {},
            icon: 'mdi-circle',
            toolTip: '',
            statusColor: row.statusColor,
            sort: row.sort
          };
        }
          
        // Add status title and count
        const value = Number(String(row.value));
        result[row.categoryId].subcategories[row.subcategoryId].statuses[row.statusColor].titles[row.statusTitle] = value;
        result[row.categoryId].subcategories[row.subcategoryId].statuses[row.statusColor].count += value;
      });
      
      // Format tooltips, icons, and links
      for (const categoryId in result) {
        for (const subcategoryId in result[categoryId].subcategories) {
          const subcategory = result[categoryId].subcategories[subcategoryId];
          
          for (const color in subcategory.statuses) {
            const status = subcategory.statuses[color];
            
            // Create tooltip
            status.toolTip = Object.entries(status.titles)
              .map(([title, count]) => `${title}: ${count}`)
              .join('\n');
            
            // Set icon based on number of distinct status titles
            status.icon = Object.keys(status.titles).length > 1 ? 'mdi-circle-multiple' : 'mdi-circle';
            
            // Generate link
            const subcategoryType = subcategory.type === 'IssueTemplate' ? 'issues' : 'processes';
            const statusType = subcategory.type === 'IssueTemplate' ? 'issue' : 'form';
            const baseLink = `https://${settings.value.apiUrl.startsWith('api-') ? settings.value.apiUrl.slice(4) : settings.value.apiUrl}/${settings.value.projectCode}/${subcategoryType}?subcategory-ids=${subcategoryId}&`;
            
            const queryParts = Object.keys(status.titles).map(title => 
              `${statusType}-statuses=${encodeURIComponent(title)}`
            );
            
            status.link = baseLink + queryParts.join('&');
          }
        }
      }
      
      return result;
    };
    
    // Main function to load all data
    const loadAllData = async () => {
      dataLoaded.value = false;
      
      try {
        // Get all data in parallel for better performance
        const [categoriesAndSubcategories, categoryCounts, subcategoryCounts] = await Promise.all([
          getCategoriesAndSubcategories(),
          getCategoryStatusCounts(),
          getSubcategoryStatusCounts(),
        ]);
        
        // Update local data
        categoriesData.value = categoriesAndSubcategories.categories;
        subcategoriesData.value = categoriesAndSubcategories.subcategories;
        
        // Update status counts
        statusCounts.value = Object.assign({}, categoryCounts);
        
        // Merge subcategory data with category data
        for (const categoryId in subcategoryCounts) {
          if (statusCounts.value[categoryId]) {
            statusCounts.value[categoryId].subcategories = subcategoryCounts[categoryId].subcategories;
          }
        }
        
      } catch (error) {
        console.error('Error loading data:', error);
      } finally {
        dataLoaded.value = true;
      }
    };
    
    // Generic query execution function
    const executeAndProcessQuery = async (query) => {
      try {
        const result = await conn.value.query(query);
        const rows = result.toArray().map((row) => {
          const jsonRow = row.toJSON();
          
          // Handle potential BigInt values by converting them to strings first
          for (const key in jsonRow) {
            // Check if the value is BigInt (will be a string representation in JSON)
            if (typeof jsonRow[key] === 'object' && jsonRow[key]?.type === 'bigint') {
              jsonRow[key] = String(jsonRow[key].value);
            }
          }
          
          return jsonRow;
        });
        
        return rows;
      } catch (error) {
        console.error('Query execution error:', error, query);
        return [];
      }
    };

    const getIconClass = (icon) => {
        if (!icon) return 'mdi-help-circle';
        
        if (icon.startsWith('mdi-')) {
            return icon; // Already in correct format for MDI
        } else if (icon.startsWith('fas fa-')) {
            // For Font Awesome 6 in Vuetify 3, format is:
            return `fa:${icon}`;
        } else if (icon.startsWith('fab fa-')) {
            return `fa:${icon}`;
        } else if (icon.startsWith('fa-')) {
            // If just fa- prefix is used, assume it's a solid icon
            return `fa:fas ${icon}`;
        } else {
            // For material icons
            return `md:${icon}`;
        }
    }
    
    // Watch for filter changes to reload data
    watch(
      () => filterContext.value.filtersLastUpdated,
      () => {
        if (isMounted.value) {
          loadAllData();
        }
      },
      { deep: true }
    );
    
    // Load data on component mount
    onMounted(async () => {
      isMounted.value = true;
    });
    </script>